import { useState } from 'react';
import { useDisconnectHelpScout } from '@/hooks/use-integrations';
import type { Integration } from '../types';
import { useHelpScoutConnection } from '../hooks/use-helpscout-connection';
import {
	HelpScoutAuthorizationScreen,
	HelpScoutConnectingScreen,
	HelpScoutManagementScreen,
	HelpScoutSuccessScreen
} from './helpscout-screens';

interface HelpScoutContentProps {
	integration: Integration;
}

export const HelpScoutContent = ({ integration }: HelpScoutContentProps) => {
	const {
		connection,
		errorMessage,
		isAuthorizing,
		showSuccessCheckpoint,
		authorize,
		dismissSuccessCheckpoint,
		markDisconnected
	} = useHelpScoutConnection(integration);
	const disconnectMutation = useDisconnectHelpScout();
	const [disconnectError, setDisconnectError] = useState<string>();

	const disconnect = (): void => {
		if (disconnectMutation.isPending) return;
		setDisconnectError(undefined);
		disconnectMutation.mutate(undefined, {
			onSuccess: markDisconnected,
			onError: () => setDisconnectError('Help Scout couldn’t be disconnected. Try again.')
		});
	};

	if (connection.status === 'connected' && showSuccessCheckpoint) {
		return <HelpScoutSuccessScreen connection={connection} onDone={dismissSuccessCheckpoint} />;
	}

	if (connection.status === 'connected') {
		return (
			<HelpScoutManagementScreen
				connection={connection}
				disconnectError={disconnectError}
				isAuthorizing={isAuthorizing}
				isDisconnecting={disconnectMutation.isPending}
				onAuthorize={() => void authorize()}
				onDisconnect={disconnect}
			/>
		);
	}

	if (connection.status === 'connecting' || isAuthorizing) return <HelpScoutConnectingScreen />;

	return (
		<HelpScoutAuthorizationScreen
			status={connection.status}
			errorMessage={errorMessage}
			isAuthorizing={isAuthorizing}
			onAuthorize={() => void authorize()}
		/>
	);
};
